import { roadSectionAtProgress } from '../shared/map-identity.js';
import { getTrackGeometry } from '../shared/track.js';
import type { BakedRoadSample } from '../shared/track.js';
import type { MapDefinition } from '../shared/types.js';

export interface MapOutlineOptions {
  /** Colour the lap by its four authored acts and number them. */
  sections?: boolean;
}

/**
 * Draws a top-down outline of a map's main road onto a small canvas, using the
 * same baked geometry the simulation and Three.js consume. Used for the garage
 * map cards and the invite summary so every district card shows its real road,
 * its lakes, its shortcuts and which way the lap runs.
 */
export function drawMapOutline(canvas: HTMLCanvasElement, map: MapDefinition, options: MapOutlineOptions = {}): void {
  const context = canvas.getContext('2d');
  if (!context) return;

  const dpr = Math.min(window.devicePixelRatio || 1, 2);
  const cssWidth = canvas.clientWidth || 260;
  const cssHeight = canvas.clientHeight || 65;
  canvas.width = Math.max(1, Math.round(cssWidth * dpr));
  canvas.height = Math.max(1, Math.round(cssHeight * dpr));

  const geometry = getTrackGeometry(map);
  const samples = geometry.main.samples;
  if (samples.length < 2) return;
  const routes = [...geometry.alternateRoutes.entries()];

  let minX = Infinity;
  let maxX = -Infinity;
  let minZ = Infinity;
  let maxZ = -Infinity;
  let widthSum = 0;
  const include = (sample: BakedRoadSample): void => {
    if (sample.x < minX) minX = sample.x;
    if (sample.x > maxX) maxX = sample.x;
    if (sample.z < minZ) minZ = sample.z;
    if (sample.z > maxZ) maxZ = sample.z;
  };
  for (const sample of samples) {
    include(sample);
    widthSum += sample.width;
  }
  for (const [, road] of routes) for (const sample of road.samples) include(sample);
  const spanX = Math.max(maxX - minX, 1);
  const spanZ = Math.max(maxZ - minZ, 1);
  const padding = (options.sections ? 18 : 10) * dpr;
  const scale = Math.min(
    (canvas.width - padding * 2) / spanX,
    (canvas.height - padding * 2) / spanZ,
  );
  const offsetX = (canvas.width - spanX * scale) / 2;
  const offsetY = (canvas.height - spanZ * scale) / 2;
  const px = (x: number) => offsetX + (x - minX) * scale;
  const py = (z: number) => offsetY + (z - minZ) * scale;

  const trace = (road: readonly BakedRoadSample[], closed: boolean): void => {
    context.beginPath();
    context.moveTo(px(road[0]!.x), py(road[0]!.z));
    for (let index = 1; index < road.length; index += 1) {
      context.lineTo(px(road[index]!.x), py(road[index]!.z));
    }
    if (closed) context.closePath();
  };

  context.clearRect(0, 0, canvas.width, canvas.height);
  context.lineJoin = 'round';
  context.lineCap = 'round';

  // Lakes sit under the road, clipped to what fits on the card.
  for (const lake of map.features.lakes ?? []) {
    context.beginPath();
    context.ellipse(px(lake.x), py(lake.z), lake.radiusX * scale, lake.radiusZ * scale, lake.rotation ?? 0, 0, Math.PI * 2);
    context.fillStyle = 'rgba(64, 160, 210, 0.32)';
    context.fill();
    context.strokeStyle = 'rgba(140, 214, 255, 0.35)';
    context.lineWidth = 1 * dpr;
    context.stroke();
  }

  // Road body: true-to-scale asphalt ribbon under the glowing centreline.
  const meanWidth = widthSum / samples.length;
  const bodyWidth = Math.max(meanWidth * scale, 3 * dpr);
  trace(samples, true);
  context.strokeStyle = 'rgba(255, 255, 255, 0.16)';
  context.lineWidth = bodyWidth;
  context.stroke();

  // Shortcuts and loops: dashed, so they read as optional.
  for (const [, road] of routes) {
    if (road.samples.length < 2) continue;
    trace(road.samples, road.closed);
    context.setLineDash([3 * dpr, 3 * dpr]);
    context.strokeStyle = 'rgba(255, 255, 255, 0.62)';
    context.lineWidth = 1.25 * dpr;
    context.stroke();
    context.setLineDash([]);
  }

  // Centreline in the map's own accent colour, or split into the lap acts.
  const sections = options.sections ? map.features.roadSections ?? [] : [];
  if (sections.length > 0) {
    const labels: { x: number; y: number; colour: string; number: number }[] = [];
    let runStart = 0;
    const sectionAt = (index: number) => roadSectionAtProgress(map, samples[index]!.progress);
    for (let index = 1; index <= samples.length; index += 1) {
      const current = sectionAt(runStart);
      if (index < samples.length && sectionAt(index) === current) continue;
      const run = samples.slice(runStart, Math.min(index + 1, samples.length));
      if (index === samples.length) run.push(samples[0]!);
      const colour = current?.accent ?? map.palette.accent;
      trace(run, false);
      context.strokeStyle = colour;
      context.lineWidth = 3 * dpr;
      context.shadowColor = colour;
      context.shadowBlur = 8 * dpr;
      context.stroke();
      context.shadowBlur = 0;
      const middle = run[Math.floor(run.length / 2)]!;
      labels.push({ x: px(middle.x), y: py(middle.z), colour, number: sections.indexOf(current!) + 1 });
      runStart = index;
    }
    // Number each act on a dark puck so it stays legible over the road.
    context.font = `800 ${10 * dpr}px system-ui, sans-serif`;
    context.textAlign = 'center';
    context.textBaseline = 'middle';
    for (const label of labels) {
      if (label.number < 1) continue;
      context.beginPath();
      context.arc(label.x, label.y, 8 * dpr, 0, Math.PI * 2);
      context.fillStyle = '#0b1015';
      context.fill();
      context.strokeStyle = label.colour;
      context.lineWidth = 1.5 * dpr;
      context.stroke();
      context.fillStyle = '#ffffff';
      context.fillText(String(label.number), label.x, label.y + 0.5 * dpr);
    }
  } else {
    trace(samples, true);
    context.strokeStyle = map.palette.accent;
    context.lineWidth = 2 * dpr;
    context.shadowColor = map.palette.accent;
    context.shadowBlur = 6 * dpr;
    context.stroke();
    context.shadowBlur = 0;
  }

  // Start/finish: a white tick across the road plus an arrow showing the
  // racing direction a few samples further on.
  const start = samples[0]!;
  const ahead = samples[Math.min(samples.length - 1, Math.max(2, Math.round(samples.length * 0.035)))]!;
  const headingX = px(ahead.x) - px(start.x);
  const headingY = py(ahead.z) - py(start.z);
  const headingLength = Math.hypot(headingX, headingY) || 1;
  const dirX = headingX / headingLength;
  const dirY = headingY / headingLength;
  const tick = Math.max(bodyWidth * 0.6, 4 * dpr);
  context.strokeStyle = '#ffffff';
  context.lineWidth = 2 * dpr;
  context.beginPath();
  context.moveTo(px(start.x) - dirY * tick, py(start.z) + dirX * tick);
  context.lineTo(px(start.x) + dirY * tick, py(start.z) - dirX * tick);
  context.stroke();

  const tipX = px(ahead.x);
  const tipY = py(ahead.z);
  const arrow = 4.5 * dpr;
  context.fillStyle = '#ffffff';
  context.beginPath();
  context.moveTo(tipX + dirX * arrow, tipY + dirY * arrow);
  context.lineTo(tipX - dirX * arrow * 0.6 - dirY * arrow * 0.75, tipY - dirY * arrow * 0.6 + dirX * arrow * 0.75);
  context.lineTo(tipX - dirX * arrow * 0.6 + dirY * arrow * 0.75, tipY - dirY * arrow * 0.6 - dirX * arrow * 0.75);
  context.closePath();
  context.fill();
}

/** Lap length in kilometres, from the same baked spline the race uses. */
export function mapLapKilometres(map: MapDefinition): number {
  return getTrackGeometry(map).main.length / 1000;
}

export interface MapBriefing {
  lapKilometres: number;
  raceKilometres: number;
  corners: number;
  climbMetres: number;
  shortcuts: number;
  loops: number;
}

/** Plain-language numbers for the selected district, measured on the same
 * baked road the race drives. */
export function mapBriefing(map: MapDefinition): MapBriefing {
  const road = getTrackGeometry(map).main;
  const samples = road.samples;
  const count = samples.length;
  const step = road.length / Math.max(1, count);
  const heading = (index: number): number => {
    const sample = samples[((index % count) + count) % count]!;
    return Math.atan2(sample.tangentX, sample.tangentZ);
  };
  // Heading change across a 30 m window centred on each sample.
  const half = Math.max(1, Math.round(15 / step));
  const turn = samples.map((_sample, index) => {
    let delta = heading(index + half) - heading(index - half);
    while (delta > Math.PI) delta -= Math.PI * 2;
    while (delta < -Math.PI) delta += Math.PI * 2;
    return Math.abs(delta);
  });
  // A corner is a local turning peak of at least ~14 degrees per 30 m that
  // dominates the road 50 m either side, so a long sweeper counts once.
  const reach = Math.max(1, Math.round(50 / step));
  let corners = 0;
  for (let index = 0; index < count; index += 1) {
    const value = turn[index]!;
    if (value < 0.24) continue;
    let peak = true;
    for (let offset = -reach; offset <= reach && peak; offset += 1) {
      if (offset === 0) continue;
      const other = turn[(((index + offset) % count) + count) % count]!;
      if (other > value || (other === value && offset < 0)) peak = false;
    }
    if (peak) corners += 1;
  }
  let minY = Infinity;
  let maxY = -Infinity;
  for (const sample of samples) {
    if (sample.y < minY) minY = sample.y;
    if (sample.y > maxY) maxY = sample.y;
  }
  const routes = map.features.alternateRoutes ?? [];
  const lapKilometres = road.length / 1000;
  return {
    lapKilometres,
    raceKilometres: lapKilometres * map.laps,
    corners: Math.max(1, corners),
    climbMetres: Math.max(0, Math.round(maxY - minY)),
    shortcuts: routes.filter((route) => route.kind === 'shortcut').length,
    loops: routes.filter((route) => route.kind === 'loop').length,
  };
}
